import axios from 'axios';
import { getActiveTenantId } from './tenantContext';
import {
  CloudAccount,
  CloudProvider,
  IngestionHealth,
  TenantUser,
  AIConfig,
  FeatureFlag,
} from '../types/admin';
import { UserRole } from '../types/auth';

const COST_API_URL = import.meta.env.VITE_COST_API_URL
  ? `${import.meta.env.VITE_COST_API_URL}/api/v1`
  : 'http://localhost:3002/api/v1';

function getAuthHeaders() {
  const token = localStorage.getItem('accessToken');
  return {
    'x-tenant-id': getActiveTenantId(),
    'x-user-id': 'user-123',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * Admin service class
 */
class AdminService {
  /**
   * Get all cloud accounts — real endpoint, matches the same account
   * identifiers the cost engine already uses elsewhere (Dashboard's Cost by
   * Account), and correctly varies by company.
   */
  async getCloudAccounts(): Promise<CloudAccount[]> {
    const response = await axios.get(`${COST_API_URL}/admin/cloud-accounts`, {
      headers: getAuthHeaders(),
      timeout: 10000,
    });
    return response.data;
  }

  /**
   * Onboard a real cloud account - calls the real POST endpoint, which
   * writes any secret (Azure's clientSecret) to Key Vault and returns a
   * generated least-privilege policy document alongside the created record.
   * Every provider now also returns a real, runnable onboarding artifact:
   * AWS gets CloudFormation + Terraform, Azure gets a role-assignment CLI
   * script, GCP gets both a Terraform snippet and a gcloud CLI script.
   */
  async createCloudAccount(
    provider: CloudProvider,
    accountName: string,
    config: Record<string, any>,
    // Multi-account onboarding fix: set by the "Onboard"/"Onboard all"
    // sibling-onboarding flow only - a normal standalone onboarding omits both.
    opts?: { accountType?: 'master' | 'member' | 'standalone'; parentCloudAccountId?: string }
  ): Promise<{ account: CloudAccount; policyDocument: string; cloudFormationTemplate?: string; terraformSnippet?: string; onboardingScript?: string; manifestSigned: boolean; manifestHash?: string; manifestSignedAt?: string; warnings?: string[] }> {
    const response = await axios.post(
      `${COST_API_URL}/admin/cloud-accounts`,
      { provider, accountName, config, ...opts },
      { headers: getAuthHeaders(), timeout: 15000 }
    );
    return response.data;
  }

  /**
   * Requirement 119/172 - checks the actually-granted permissions cover
   * what the platform needs, BEFORE the account is created. Called with
   * the same provider+config the wizard is about to submit.
   */
  async checkOnboardingPreflight(provider: CloudProvider, config: Record<string, any>): Promise<{ ok: boolean; notAvailable: boolean; results: { action: string; allowed: boolean; label?: string }[]; message: string }> {
    const response = await axios.post(
      `${COST_API_URL}/admin/cloud-accounts/preflight`,
      { provider, config },
      { headers: getAuthHeaders(), timeout: 20000 }
    );
    return response.data;
  }

  /**
   * Requirement 119/172 - AWS Organizations / Azure Management Group / GCP
   * Folder sibling-account discovery for an already-onboarded account.
   */
  async discoverCloudAccountHierarchy(id: string): Promise<{ notConfigured: boolean; message?: string; accounts: { provider: string; accountId: string; name: string; parentId?: string }[]; alreadyOnboarded?: string[] }> {
    const response = await axios.get(`${COST_API_URL}/admin/cloud-accounts/${id}/discover-hierarchy`, {
      headers: getAuthHeaders(),
      timeout: 20000,
    });
    return response.data;
  }

  /**
   * Re-run the real connection check for an account ("Verify Now") -
   * useful once Key Vault access is granted for an account that was stuck
   * on "pending verification".
   */
  async verifyCloudAccount(id: string): Promise<{ account: CloudAccount }> {
    const response = await axios.post(
      `${COST_API_URL}/admin/cloud-accounts/${id}/verify`,
      {},
      { headers: getAuthHeaders(), timeout: 20000 }
    );
    return response.data;
  }

  /**
   * FR-ONB-005 Phase 1: the single CloudFormation StackSet the customer runs
   * once in their management account to create the FinOps role across every
   * member account of an OU - instead of applying a per-account template once
   * per account by hand. Read-only; provisions nothing by itself.
   */
  async getMemberStackSetTemplate(masterCloudAccountId: string): Promise<{ stackSetTemplate: string; roleName: string }> {
    const response = await axios.get(
      `${COST_API_URL}/admin/cloud-accounts/${masterCloudAccountId}/member-stackset-template`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * FR-ONB-005 Phase 2: the platform runs the StackSet itself. Returns
   * ok:false with needsSetup when the role lacks the opt-in permission or the
   * organization hasn't enabled StackSets trusted access - both are expected
   * outcomes with actionable messages, not server errors.
   */
  async deployMemberStackSet(masterCloudAccountId: string): Promise<{
    ok: boolean;
    operationId?: string;
    stackSetName?: string;
    message: string;
    needsSetup?: boolean;
  }> {
    const response = await axios.post(
      `${COST_API_URL}/admin/cloud-accounts/${masterCloudAccountId}/deploy-member-stackset`,
      {},
      { headers: getAuthHeaders(), timeout: 60000 }
    );
    return response.data;
  }

  async getStackSetOperationStatus(
    masterCloudAccountId: string,
    operationId: string
  ): Promise<{ status: string; instances: { accountId: string; status: string; statusReason?: string }[] }> {
    const response = await axios.get(
      `${COST_API_URL}/admin/cloud-accounts/${masterCloudAccountId}/stackset-operation/${operationId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * Remove a cloud account (and its Key Vault secret, if any). Returns a
   * real teardown script (Requirement 119/172 offboarding) reversing what
   * onboarding granted in the customer's own account - we never call out to
   * reverse it ourselves, same trust model as onboarding.
   */
  async deleteCloudAccount(id: string): Promise<{ deleted: boolean; teardownScript?: string }> {
    const response = await axios.delete(`${COST_API_URL}/admin/cloud-accounts/${id}`, {
      headers: getAuthHeaders(),
      timeout: 10000,
    });
    return response.data;
  }

  /**
   * Get real ingestion health for all cloud accounts (Requirement 172 AC6) -
   * built on the same cloud_accounts data Cloud Onboarding verifies, not a
   * separate mock.
   */
  async getIngestionHealth(): Promise<IngestionHealth[]> {
    const response = await axios.get(`${COST_API_URL}/admin/ingestion-health`, {
      headers: getAuthHeaders(),
      timeout: 15000,
    });
    return response.data;
  }

  /**
   * Requirement 36 / FR-ONB-011/012 - real Compute Optimizer enrollment +
   * Trusted Advisor reachability check for an onboarded AWS account.
   */
  async enableAwsOptimization(cloudAccountId: string): Promise<{
    computeOptimizer: { enrolled: boolean; available: boolean; message: string };
    trustedAdvisor: { available: boolean; message: string };
  }> {
    const response = await axios.post(
      `${COST_API_URL}/admin/cloud-accounts/${cloudAccountId}/enable-aws-optimization`,
      {},
      { headers: getAuthHeaders(), timeout: 20000 }
    );
    return response.data;
  }

  /**
   * Requirement 119 AC3 - real measured time-to-first-value /
   * time-to-first-automated-savings, computed server-side from
   * cloud_accounts + execution_audit, not a static target claim.
   */
  async getOnboardingMetrics(): Promise<{
    firstAccountCreatedAt: string | null;
    firstVerifiedAt: string | null;
    timeToFirstValueHours: number | null;
    timeToFirstValueDays: number | null;
    timeToFirstValueTargetMet: boolean | null;
    firstAutomatedSavingsAt: string | null;
    timeToFirstAutomatedSavingsDays: number | null;
    timeToFirstAutomatedSavingsTargetMet: boolean | null;
  }> {
    const response = await axios.get(`${COST_API_URL}/admin/onboarding-metrics`, {
      headers: getAuthHeaders(),
      timeout: 10000,
    });
    return response.data;
  }

  /**
   * Get all tenant users — real Keycloak-backed API. Previously this
   * silently fell back to 4 hardcoded mock users on any error (network
   * blip, auth failure, anything) with no indication to the admin that
   * what they're looking at isn't real - letting the error propagate lets
   * the tab's existing ErrorState UI show a real "failed to load" message
   * instead of a fake user list that looks legitimate.
   */
  async getTenantUsers(): Promise<TenantUser[]> {
    const response = await axios.get(`${COST_API_URL}/admin/users`, {
      headers: getAuthHeaders(),
      timeout: 10000,
    });
    return response.data.data || [];
  }

  /**
   * Every user's tenant entitlements, plus the list of tenants that exist and
   * whether enforcement is currently switched on.
   *
   * One call for the whole table rather than one per user: User Management
   * renders every user at once, and an N+1 here would be N+1 round trips to a
   * Keycloak-backed endpoint.
   */
  async getTenantAccess(): Promise<{
    tenants: Array<{ id: string; label: string }>;
    access: Record<string, string[]>;
    strict: boolean;
  }> {
    const response = await axios.get(`${COST_API_URL}/admin/tenant-access`, {
      headers: getAuthHeaders(),
      timeout: 10000,
    });
    return response.data;
  }

  /**
   * Turns an axios failure into the server's actual message.
   *
   * Without this the UI shows "Request failed with status code 500", which
   * says nothing - and container log streaming is unavailable here because the
   * corporate proxy TLS-intercepts Azure's log endpoints, so the response body
   * is the only place the real cause can be read.
   */
  private tenantAccessError(e: any): Error {
    const detail = e?.response?.data?.detail || e?.response?.data?.error;
    return new Error(detail ? `${detail}` : e?.message || 'Unknown error');
  }

  /** Replaces one user's entitlements with exactly this set. */
  async setTenantAccess(userId: string, tenantIds: string[]): Promise<void> {
    try {
      await axios.put(
        `${COST_API_URL}/admin/tenant-access/${userId}`,
        { tenantIds },
        { headers: getAuthHeaders(), timeout: 10000 }
      );
    } catch (e: any) {
      throw this.tenantAccessError(e);
    }
  }

  /** Grants all tenants to users who have no entitlements yet. Idempotent. */
  async seedTenantAccess(): Promise<{
    seeded: number;
    seededUsers: string[];
    skippedAlreadyProvisioned: number;
    totalKeycloakUsers: number;
  }> {
    try {
      const response = await axios.post(
        `${COST_API_URL}/admin/tenant-access/sync`,
        {},
        { headers: getAuthHeaders(), timeout: 30000 }
      );
      return response.data;
    } catch (e: any) {
      throw this.tenantAccessError(e);
    }
  }

  /**
   * Get all integrations - real status from the backend (Jira is actually
   * wired; Slack/Teams/ServiceNow are honestly reported as not implemented).
   */
  async getIntegrations(): Promise<any[]> {
    const response = await axios.get(`${COST_API_URL}/admin/integrations`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  }

  /**
   * Get AI configuration — calls real DB-backed API
   */
  async getAIConfig(): Promise<AIConfig> {
    const response = await axios.get(`${COST_API_URL}/admin/ai-config`, {
      headers: getAuthHeaders(),
      timeout: 10000,
    });
    return response.data;
  }

  /**
   * Get feature flags — calls real DB-backed API
   */
  async getFeatureFlags(): Promise<FeatureFlag[]> {
    const response = await axios.get(`${COST_API_URL}/admin/feature-flags`, {
      headers: getAuthHeaders(),
      timeout: 10000,
    });
    return response.data;
  }

  /**
   * Update AI configuration — calls real DB-backed API
   */
  async updateAIConfig(config: Partial<AIConfig>): Promise<AIConfig> {
    const response = await axios.patch(`${COST_API_URL}/admin/ai-config`, config, {
      headers: getAuthHeaders(),
      timeout: 10000,
    });
    return response.data;
  }

  /**
   * Requirement 49 AC3/AC4 - the L1 real-execution kill switch, SEPARATE
   * from AI config's kill switch (that one gates LLM calls; this one gates
   * real cloud mutation). Defaults to off for every tenant.
   */
  async getL1ExecutionConfig(): Promise<{ enabled: boolean }> {
    const response = await axios.get(`${COST_API_URL}/admin/l1-execution`, {
      headers: getAuthHeaders(),
      timeout: 10000,
    });
    return response.data;
  }

  /**
   * Turning this on returns the additional AWS write-permission policy the
   * tenant must separately apply before any L1 action can actually run.
   */
  async updateL1ExecutionConfig(enabled: boolean): Promise<{ enabled: boolean; additionalPolicy: string | null; message: string }> {
    const response = await axios.patch(
      `${COST_API_URL}/admin/l1-execution`,
      { enabled },
      { headers: getAuthHeaders(), timeout: 10000 }
    );
    return response.data;
  }

  /**
   * Toggle feature flag — calls real DB-backed API
   */
  async toggleFeatureFlag(flagId: string, enabled: boolean): Promise<FeatureFlag> {
    const response = await axios.patch(
      `${COST_API_URL}/admin/feature-flags/${flagId}`,
      { enabled },
      { headers: getAuthHeaders(), timeout: 10000 }
    );
    return response.data;
  }

  /**
   * Invite user — calls real DB API
   */
  async inviteUser(
    email: string,
    roles: UserRole[]
  ): Promise<TenantUser & { inviteMessage?: string; tempPassword?: string; emailSent?: boolean }> {
    const role = roles[0] === UserRole.ADMIN ? 'admin' : roles[0] === UserRole.ANALYST ? 'analyst' : 'viewer';
    const response = await axios.post(
      `${COST_API_URL}/admin/users/invite`,
      // Deliberately NOT sending a `name`. The dialog never collects one, and
      // passing the raw email local part ("shreyas.bhagat") made the backend
      // treat it as an explicit single-token name, producing firstName
      // "Shreyas.bhagat" / lastName "Bhagat". The backend derives both fields
      // from the email properly when no name is supplied.
      { email, role },
      { headers: getAuthHeaders(), timeout: 10000 }
    );
    // Backend reports whether the welcome email actually sent (SMTP can be
    // unavailable) - surface it to the caller instead of assuming success.
    // tempPassword is lifted out of the envelope so the caller can render it
    // in a copyable field rather than only inside the prose message.
    return {
      ...response.data.data,
      inviteMessage: response.data.message,
      // TOP-LEVEL on the response body, NOT inside `data` - the backend
      // returns { data: {...}, tempPassword, emailSent, message }. Reading
      // response.data.data.tempPassword always yielded undefined, so the UI
      // silently fell back to alert()-ing the prose message, which is exactly
      // the non-copyable native popup this field exists to avoid.
      tempPassword: response.data.tempPassword,
      emailSent: response.data.emailSent,
    };
  }

  /**
   * Update user roles — calls real DB API
   */
  async updateUserRoles(userId: string, roles: UserRole[]): Promise<TenantUser> {
    const role = roles[0] === UserRole.ADMIN ? 'admin' : roles[0] === UserRole.ANALYST ? 'analyst' : 'viewer';
    const response = await axios.put(
      `${COST_API_URL}/admin/users/${userId}/role`,
      { role },
      { headers: getAuthHeaders(), timeout: 10000 }
    );
    return response.data.data;
  }

  /**
   * Remove user — calls real DB API (soft delete / disable)
   */
  async removeUser(userId: string): Promise<void> {
    await axios.delete(`${COST_API_URL}/admin/users/${userId}`, {
      headers: getAuthHeaders(),
      timeout: 10000,
    });
  }

  /**
   * Re-enable a disabled user — the inverse of removeUser.
   */
  async enableUser(userId: string): Promise<void> {
    await axios.post(
      `${COST_API_URL}/admin/users/${userId}/enable`,
      {},
      { headers: getAuthHeaders(), timeout: 10000 }
    );
  }

  /**
   * Permanently delete a user — frees up their email/username for a fresh
   * Invite. Unlike removeUser (disable), this is irreversible.
   */
  async deleteUserPermanently(userId: string): Promise<void> {
    await axios.delete(`${COST_API_URL}/admin/users/${userId}/permanent`, {
      headers: getAuthHeaders(),
      timeout: 10000,
    });
  }

  /**
   * Admin reset password — calls real DB API
   */
  async resetUserPassword(userId: string, tempPassword: string): Promise<void> {
    await axios.post(
      `${COST_API_URL}/admin/users/${userId}/reset-password`,
      { tempPassword },
      { headers: getAuthHeaders(), timeout: 10000 }
    );
  }

  /**
   * Add a real Jira or Teams integration for this tenant - calls the real
   * POST endpoint, which writes any secret (Jira API token, Teams webhook
   * URL) to Key Vault and returns the created record.
   */
  async createIntegration(type: 'jira' | 'teams' | 'servicenow', name: string, config: Record<string, any>): Promise<any> {
    const response = await axios.post(
      `${COST_API_URL}/admin/integrations`,
      { type, name, config },
      { headers: getAuthHeaders(), timeout: 15000 }
    );
    return response.data;
  }

  /**
   * Remove an integration (and its Key Vault secret, if any).
   */
  async deleteIntegration(id: string): Promise<void> {
    await axios.delete(`${COST_API_URL}/admin/integrations/${id}`, {
      headers: getAuthHeaders(),
      timeout: 10000,
    });
  }

  /**
   * Test integration connection - real HTTP call to Jira/Teams, not a mock.
   */
  async testIntegration(integrationId: string): Promise<{ success: boolean; message: string }> {
    const response = await axios.post(
      `${COST_API_URL}/admin/integrations/${integrationId}/test`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  }
}

export const adminService = new AdminService();
